import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Users, DollarSign, Activity, Crown, Loader2, Trash2, ShieldCheck, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userTab, setUserTab] = useState("users");
  const [pendingChanges, setPendingChanges] = useState({});

  const hasPending = Object.keys(pendingChanges).length > 0;

  const load = async () => {
    try {
      // Verify admin role fresh from server on every load
      const me = await api.get("/auth/me");
      if (me.data.role !== "admin") {
        toast.error("Access denied.");
        nav("/app", { replace: true });
        return;
      }
      const [s, u, t] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/transactions"),
      ]);
      setStats(s.data); setUsers(u.data); setTxns(t.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Access denied.");
        nav("/app", { replace: true });
      } else {
        toast.error("Failed to load admin");
      }
    }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const stagePlan = (uid, plan) => {
    const original = users.find(u => u.id === uid)?.plan;
    setPendingChanges(prev => {
      const next = { ...prev };
      if (plan === original) delete next[uid];
      else next[uid] = plan;
      return next;
    });
  };

  const saveChanges = async () => {
    if (!hasPending) return;
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(pendingChanges).map(([uid, plan]) =>
          api.patch(`/admin/users/${uid}/plan?plan=${plan}`)
        )
      );
      const count = Object.keys(pendingChanges).length;
      setPendingChanges({});
      toast.success(`${count} change${count > 1 ? "s" : ""} saved`);
      load();
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setPendingChanges({});
    toast("Changes discarded");
  };

  const deleteTxn = async (sessionId) => {
    try {
      await api.delete(`/admin/transactions/${sessionId}`);
      setTxns((prev) => prev.filter((t) => t.session_id !== sessionId));
      toast.success("Transaction deleted");
    } catch { toast.error("Failed to delete transaction"); }
  };

  if (loading) return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin text-neutral-500" /></div>;

  return (
    <div className="px-10 py-10" data-testid="admin-page">
      <div className="flex items-start justify-between mb-2">
        <h1 className="font-display text-5xl font-black tracking-tighter">Super Admin</h1>
        <div className="flex items-center gap-2 mt-1.5">
          {hasPending && (
            <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/30 bg-amber-400/5 px-2.5 py-1.5">
              {Object.keys(pendingChanges).length} unsaved
            </span>
          )}
          <button
            onClick={discardChanges}
            disabled={!hasPending}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-white/15 text-neutral-400 hover:text-white hover:border-white/30 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <X className="w-3.5 h-3.5" /> Discard
          </button>
          <button
            onClick={saveChanges}
            disabled={!hasPending || saving}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-[#D3FF24] text-black hover:brightness-110 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
      <p className="text-neutral-500 mb-10">Platform metrics and user management.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-12">
        {[
          { label: "Total users", val: stats.total_users, icon: Users, color: "text-[#0055FF]" },
          { label: "Sessions", val: stats.total_sessions, icon: Activity, color: "text-[#D3FF24]" },
          { label: "Paid users", val: stats.pro_users + stats.premium_users, icon: Crown, color: "text-[#00A3FF]" },
          { label: "Revenue", val: `$${(stats.revenue || 0).toFixed(2)}`, icon: DollarSign, color: "text-[#D3FF24]" },
        ].map((s) => (
          <div key={s.label} className="bg-[#0A0A0A] p-6">
            <s.icon className={`w-5 h-5 mb-4 ${s.color}`} strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{s.label}</div>
            <div className="font-display text-3xl font-bold">{s.val}</div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-2xl font-bold mb-4">Users</h2>
      <div className="mb-12">
        <div className="flex border-b border-white/10 mb-0">
          {[
            { id: "users", label: "Users", icon: Users },
            { id: "admins", label: "Admins", icon: ShieldCheck },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setUserTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${
                userTab === tab.id
                  ? "border-[#D3FF24] text-white"
                  : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
              {tab.label}
              <span className={`ml-1 px-1.5 py-0.5 text-[10px] rounded ${userTab === tab.id ? "bg-[#D3FF24]/20 text-[#D3FF24]" : "bg-white/5 text-neutral-500"}`}>
                {users.filter(u => tab.id === "admins" ? u.role === "admin" : u.role !== "admin").length}
              </span>
            </button>
          ))}
        </div>
        <div className="border border-t-0 border-white/10 overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-[#0F0F0F] text-[10px] uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.filter(u => userTab === "admins" ? u.role === "admin" : u.role !== "admin").map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3 text-neutral-400">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${u.role==="admin" ? "bg-[#D3FF24]/20 text-[#D3FF24]" : "bg-white/5 text-neutral-400"}`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={pendingChanges[u.id] ?? u.plan}
                      onChange={(e) => stagePlan(u.id, e.target.value)}
                      data-testid={`plan-${u.id}`}
                      className={`bg-black px-2 py-1 text-xs border ${pendingChanges[u.id] ? "border-amber-400/60 text-amber-400" : "border-white/10"}`}
                    >
                      <option value="free" className="bg-black text-white">free</option>
                      <option value="pro" className="bg-black text-white">pro</option>
                      <option value="elite" className="bg-black text-white">elite</option>
                      <option value="admin" className="bg-black text-white">admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-neutral-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {users.filter(u => userTab === "admins" ? u.role === "admin" : u.role !== "admin").length === 0 && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-neutral-600 text-sm">No {userTab === "admins" ? "admin" : "regular"} users yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="font-display text-2xl font-bold mb-4">Transactions</h2>
      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F] text-[10px] uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3">Session</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 && (<tr><td colSpan="6" className="px-4 py-8 text-center text-neutral-600 text-sm">No transactions yet</td></tr>)}
            {txns.map((t) => (
              <tr key={t.session_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-xs font-mono text-neutral-400">{t.session_id.slice(-12)}</td>
                <td className="px-4 py-3">{t.lookup_key}</td>
                <td className="px-4 py-3">${t.amount?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${t.payment_status==="paid" ? "bg-[#D3FF24]/20 text-[#D3FF24]" : "bg-white/5 text-neutral-400"}`}>{t.payment_status}</span>
                </td>
                <td className="px-4 py-3 text-neutral-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => deleteTxn(t.session_id)}
                    className="p-1.5 text-neutral-600 hover:text-[#FF3B30] hover:bg-white/5 transition-colors"
                    title="Delete transaction"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
